import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SearchEvent } from '../api/searchTypes';
import { RESOLUTION_OPTIONS } from '../../components/charts/convergence/constants';
import { useMultiMarketProbability } from '../../api/useMultiMarketProbability';
import { assignMarketColors } from '../../utils/marketColors';
import { ChartStack } from '../../event/chart/ChartStack';
import { ChartToolbar } from '../../event/chart/ChartToolbar';
import {
    probabilitySeries,
    type ProbabilityMode,
} from '../../event/chart/series/probability';
import { volumeSeries } from '../../event/chart/series/volume';
import type { ChartSeries } from '../../event/chart/seriesTypes';
import {
    isQuickRange,
    lifetimeOf,
    QUICK_RANGES,
    quickRange,
} from '../../event/lifetime';
import type { Outcome } from '../../event/model/types';
import {
    MAX_SHOWN_ACTIVE,
    MAX_SHOWN_CLOSED,
} from '../../event/outcomes/ranking';
import { resolutionLabelOf } from '../../event/useEventWindow';
import { bucketedNow, pickResolution } from '../../event/window';
import { isDependent } from './marketOrder';
import { marketSpan, spanBounds } from './searchExport';

const CHART_HEIGHT = 300;
const MIN_CHART_HEIGHT = 240;
const FULLSCREEN_CHROME = 96;
const AVAILABLE_RESOLUTIONS = RESOLUTION_OPTIONS.map(o => o.value);

export interface RowChartPanelProps {
    event: SearchEvent;
    marketId?: string | null;
}

function parseDate(value: string | null): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
}

function useWindowHeight(): number {
    const [height, setHeight] = useState(() => window.innerHeight);
    useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return height;
}

export function RowChartPanel({ event, marketId = null }: RowChartPanelProps) {
    const theme = useTheme();

    const markets = useMemo(
        () =>
            marketId
                ? event.markets.filter(m => m.market_id === marketId)
                : event.markets,
        [event.markets, marketId]
    );
    const single = markets.length === 1;

    const chartMarkets = useMemo(() => {
        const byProbability = [...markets].sort(
            (a, b) => (b.yes_prob ?? 0) - (a.yes_prob ?? 0)
        );
        const open = byProbability.filter(m => m.is_open);
        const closed = byProbability.filter(m => !m.is_open);
        return [...open, ...closed].slice(
            0,
            open.length > 0 ? MAX_SHOWN_ACTIVE : MAX_SHOWN_CLOSED
        );
    }, [markets]);

    const dependent = useMemo(
        () => !single && isDependent(markets.filter(m => m.yes_prob != null)),
        [markets, single]
    );

    const outcomes = useMemo<Outcome[]>(
        () =>
            chartMarkets.map((m, order) => ({
                id: m.market_id,
                label:
                    m.group_item_title ||
                    m.outcome_yes_label ||
                    m.question ||
                    m.market_id,
                question: m.question ?? '',
                value: null,
                order,
                image: m.market_image,
                quotes: [
                    {
                        outcomeId: m.market_id,
                        source: 'polymarket',
                        market: { source: 'polymarket', marketId: m.market_id },
                        probability: m.yes_prob,
                        volume: m.volume,
                        closed: !m.is_open,
                        startDate: null,
                        endDate: null,
                    },
                ],
            })),
        [chartMarkets]
    );

    const marketIds = useMemo(
        () => chartMarkets.map(m => m.market_id),
        [chartMarkets]
    );
    const colors = useMemo(() => assignMarketColors(marketIds), [marketIds]);

    const [nowMs] = useState(() => bucketedNow());
    const bounds = useMemo(
        () =>
            spanBounds(
                chartMarkets.map(m => marketSpan(m, event)),
                nowMs
            ),
        [chartMarkets, event, nowMs]
    );
    const closed = chartMarkets.every(m => !m.is_open);

    const [range, setRange] = useState<[Date, Date] | null>(null);
    const [resolutionChoice, setResolutionChoice] = useState<number | null>(
        null
    );
    const fetchRange = range ?? bounds;
    const resolution = useMemo(
        () =>
            pickResolution(
                fetchRange,
                marketIds.length,
                AVAILABLE_RESOLUTIONS,
                resolutionChoice
            ),
        [fetchRange, marketIds.length, resolutionChoice]
    );

    const { series: candles, loading } = useMultiMarketProbability(
        marketIds,
        fetchRange[0],
        fetchRange[1],
        resolution
    );

    const boundsEndMs = bounds[1].getTime();
    const plannedEnd = single ? markets[0].end_date : event.end_date;
    const lifetime = useMemo(
        () =>
            lifetimeOf(
                bounds[0],
                parseDate(plannedEnd),
                closed,
                closed ? boundsEndMs : Date.now()
            ),
        [bounds, plannedEnd, closed, boundsEndMs]
    );

    const [lastCandleMs, setLastCandleMs] = useState<number | null>(null);
    useEffect(() => {
        if (range !== null) return;
        let last: number | null = null;
        for (const s of candles) {
            const t = s.data[s.data.length - 1]?.t.getTime();
            if (t !== undefined && (last === null || t > last)) last = t;
        }
        if (last !== null) setLastCandleMs(last);
    }, [candles, range]);
    const anchorMs =
        closed && lastCandleMs !== null ? lastCandleMs : boundsEndMs;

    const quickRanges = useMemo(
        () => [
            ...QUICK_RANGES.map(q => ({
                id: q.id,
                label: q.label,
                disabled: quickRange(q.spanMs, bounds, anchorMs) === null,
                active: isQuickRange(range, q.spanMs, anchorMs),
            })),
            {
                id: 'all',
                label: 'All',
                disabled: false,
                active: range === null,
            },
        ],
        [bounds, anchorMs, range]
    );
    const onQuickRange = useCallback(
        (id: string) => {
            const choice = QUICK_RANGES.find(q => q.id === id);
            setRange(
                choice ? quickRange(choice.spanMs, bounds, anchorMs) : null
            );
        },
        [bounds, anchorMs]
    );

    const [chosenMode, setChosenMode] = useState<ProbabilityMode | null>(null);
    const [showVolume, setShowVolume] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const candlesAllowed = outcomes.length === 1;
    const stackedAllowed = outcomes.length === 1 || dependent;
    const mode: ProbabilityMode =
        chosenMode ?? (single ? 'candles' : dependent ? 'stacked' : 'line');
    const chartMode: ProbabilityMode =
        (mode === 'candles' && !candlesAllowed) ||
        (mode === 'stacked' && !stackedAllowed)
            ? 'line'
            : mode;

    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const windowHeight = useWindowHeight();
    const availableHeight = fullscreen
        ? Math.max(MIN_CHART_HEIGHT, windowHeight - FULLSCREEN_CHROME)
        : CHART_HEIGHT;

    const series = useMemo<ChartSeries[]>(() => {
        const list = probabilitySeries(
            outcomes,
            candles,
            colors,
            chartMode,
            false,
            theme.palette.text.secondary
        );
        if (showVolume) {
            list.push(volumeSeries(candles, theme.palette.text.primary));
        }
        return list;
    }, [
        outcomes,
        candles,
        colors,
        chartMode,
        showVolume,
        theme.palette.text.primary,
        theme.palette.text.secondary,
    ]);

    return (
        <Box
            sx={
                fullscreen
                    ? {
                          position: 'fixed',
                          inset: 0,
                          zIndex: theme.zIndex.modal,
                          p: 2,
                          overflowY: 'auto',
                          bgcolor: 'background.default',
                      }
                    : { pb: 1 }
            }
        >
            <ChartToolbar
                displayRange={range ?? bounds}
                zoomed={range !== null}
                quickRanges={quickRanges}
                onQuickRange={onQuickRange}
                mode={chartMode}
                candlesAllowed={candlesAllowed}
                stackedAllowed={stackedAllowed}
                onModeChange={setChosenMode}
                resolutionChoice={resolutionChoice}
                resolutionLabel={resolutionLabelOf(resolution)}
                onResolutionChange={setResolutionChoice}
                showVolume={showVolume}
                onToggleVolume={() => setShowVolume(v => !v)}
                fullscreen={fullscreen}
                onToggleFullscreen={() => setFullscreen(v => !v)}
            />
            <ChartStack
                series={series}
                bounds={bounds}
                range={range}
                onRangeChange={setRange}
                availableHeight={availableHeight}
                loading={loading}
                lifetime={lifetime}
                rangeLabel={quickRanges.find(q => q.active)?.label ?? null}
            />
        </Box>
    );
}
